import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BOOKING_URL } from "@/data/services";
import { Sprig } from "./Logo";
import { Reveal, EASE } from "./Reveal";

export default function ServiceLayout({ eyebrow, title, lead, image, imageAlt, children, formal = false }) {
  return (
    <main data-testid="service-page">
      <section className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute -right-10 -top-10 text-euca-badge">
          <Sprig className="h-64 w-64" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 relative">
          {eyebrow && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="text-[11px] font-semibold uppercase tracking-[0.3em] text-euca"
            >
              {eyebrow}
            </motion.p>
          )}
          <span className="mask-line mt-4">
            <motion.span
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            >
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-euca-dark max-w-3xl">
                {title}
              </h1>
            </motion.span>
          </span>
          {lead && (
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
              className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-mutedsoft"
            >
              {lead}
            </motion.p>
          )}
          {formal && (
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
              className="mt-10 h-px w-32 origin-left bg-euca"
            />
          )}
        </div>
      </section>

      {image && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] border border-euca-line">
              <img src={image} alt={imageAlt || title} loading="lazy" className="h-64 w-full object-cover sm:h-96" />
            </div>
          </Reveal>
        </section>
      )}

      {children}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28">
        <Reveal>
          <div className="rounded-[2rem] bg-euca-dark px-6 py-14 sm:px-14 text-center" data-testid="service-cta">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-euca-sage">Take the next step</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-medium text-euca-mist">
              Ready to talk it through?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-euca-mist/70">
              Send an enquiry or book an appointment and we will help you identify the most appropriate next step.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a
                href="/book"
                data-testid="service-book-button"
                className="rounded-full bg-euca-mist px-7 py-3.5 text-sm font-semibold text-euca-dark transition-all duration-300 hover:bg-white"
              >
                Book an Appointment
              </a>
              <Link
                to="/contact"
                data-testid="service-enquiry-button"
                className="rounded-full border border-euca-mist/40 px-7 py-3.5 text-sm font-semibold text-euca-mist transition-all duration-300 hover:border-euca-mist hover:text-white"
              >
                Send an Enquiry
              </Link>
              <Link
                to="/fees-funding"
                data-testid="service-fees-link"
                className="rounded-full border border-euca-mist/40 px-7 py-3.5 text-sm font-semibold text-euca-mist transition-all duration-300 hover:border-euca-mist hover:text-white"
              >
                View Fees &amp; Funding
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}

export function ContentSection({ eyebrow, title, children, pale = false }) {
  return (
    <section className={pale ? "bg-white" : ""}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        {(eyebrow || title) && (
          <Reveal className="max-w-3xl">
            {eyebrow && <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-euca">{eyebrow}</p>}
            {title && <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-medium tracking-tight text-euca-dark">{title}</h2>}
          </Reveal>
        )}
        <div className={title ? "mt-10" : ""}>{children}</div>
      </div>
    </section>
  );
}
